import React, { useEffect, useState } from "react";

import { toast } from "react-toastify";
import { Loader2 } from "lucide-react";

import { Button } from "../ui/button";
import toastError from "../../errors/toastError";
import api from "../../services/api";

// O que a IA pediu e que ainda não saiu: uma ação que muda alguma coisa
// (segunda via de boleto, transferência de fila) esperando o atendente
// decidir.
//
// A consulta é repetida de 15 em 15 segundos porque o worker não tem
// Socket.IO: o evento de tempo real não existe para este caso.

const AiPendingActionCard = ({ ticketId }) => {
  const [pendencias, setPendencias] = useState([]);
  // A ação e a decisão em curso: só o botão clicado mostra o giro.
  const [decidindo, setDecidindo] = useState(null);

  useEffect(() => {
    // Guardado para que o erro possa parar o polling: sem esta referência o
    // catch não alcança o intervalo criado logo abaixo.
    let intervalo = null;

    const buscar = () => {
      api
        .get("/ai-pending-actions", { params: { ticketId } })
        .then(({ data }) =>
          setPendencias(Array.isArray(data) ? data : [])
        )
        .catch(err => {
          // Empresa sem o módulo de agentes de IA não tem pendência: o cartão
          // some em silêncio, sem alarme a cada 15 segundos.
          setPendencias([]);

          // 403 aqui é módulo não contratado ou falta de permissão — condição
          // que não muda com o tempo. Insistir é bater no servidor a cada 15s
          // para sempre sem chance de resposta diferente.
          if (err?.response?.status === 403 && intervalo) {
            clearInterval(intervalo);
          }
        });
    };

    buscar();

    intervalo = setInterval(buscar, 15000);

    return () => clearInterval(intervalo);
  }, [ticketId]);

  const decidir = async (id, decisao) => {
    setDecidindo({ id, decisao });

    try {
      await api.post(`/ai-pending-actions/${id}/${decisao}`);

      toast.success(
        decisao === "approve" ? "Ação aprovada." : "Ação recusada."
      );
    } catch (err) {
      // 409: outra pessoa decidiu antes, ou a ação venceu. Recarregar mostra
      // o estado certo — insistir na decisão seria brigar com o servidor.
      if (err?.response?.status === 409) {
        toast.error("Esta ação já foi decidida.");
      } else {
        toastError(err);
      }
    }

    setDecidindo(null);

    try {
      const { data } = await api.get("/ai-pending-actions", {
        params: { ticketId },
      });
      setPendencias(Array.isArray(data) ? data : []);
    } catch {
      setPendencias([]);
    }
  };

  if (!pendencias.length) return null;

  return (
    <div className="flex-none space-y-2 px-3 pt-2">
      {pendencias.map((pendencia) => (
        <div
          key={pendencia.id}
          className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3"
        >
          <p className="text-sm leading-relaxed">
            🤖 A IA quer executar:{" "}
            <span className="font-medium">
              {pendencia.tool?.description || pendencia.tool?.name}
            </span>
          </p>

          {Object.keys(pendencia.params || {}).length > 0 && (
            <ul className="mt-1.5 space-y-0.5 text-xs text-muted-foreground">
              {Object.entries(pendencia.params).map(([chave, valor]) => (
                <li key={chave}>
                  <span className="font-medium text-foreground">{chave}:</span>{" "}
                  {String(valor)}
                </li>
              ))}
            </ul>
          )}

          <div className="mt-2.5 flex gap-2">
            <Button
              size="sm"
              className="h-8 text-xs"
              disabled={decidindo?.id === pendencia.id}
              onClick={() => decidir(pendencia.id, "approve")}
            >
              {decidindo?.id === pendencia.id &&
                decidindo.decisao === "approve" && (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                )}
              Aprovar
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-8 text-xs"
              disabled={decidindo?.id === pendencia.id}
              onClick={() => decidir(pendencia.id, "reject")}
            >
              {decidindo?.id === pendencia.id &&
                decidindo.decisao === "reject" && (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                )}
              Recusar
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
};

export default AiPendingActionCard;
